import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Banknote,
  ChevronDown,
  CreditCard,
  Info,
  Repeat,
  Ticket,
  Users,
} from "lucide-react";
import { gauntlet } from "../lib/api";
import { cx } from "../lib/format";
import { Badge, Button, Callout, Panel, Skeleton } from "../components/ui";

const KIND_ICON = {
  hosted: CreditCard,
  p2p: Users,
  voucher: Ticket,
  swap: Repeat,
} as const;

/**
 * Adding fiat to the wallet.
 *
 * This page is a signpost, not a broker. Gauntlet never touches fiat, holds no
 * balance and takes no fee — which is both the honest description and the thing
 * that keeps a self-hosted wallet out of financial regulation.
 *
 * It states the KYC position up front rather than letting users hunt for a
 * no-verification card purchase that does not lawfully exist.
 */
export function AddFundsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const { data, isLoading } = useQuery({
    queryKey: ["onramp-options"],
    queryFn: () => gauntlet.onrampOptions(),
    staleTime: Infinity,
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const hosted = data?.hosted ?? [];
  const noKyc = data?.noKyc ?? [];
  const swaps = data?.swaps ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Add funds</h1>
        <p className="mt-1 text-sm text-ink-dim">
          Ways to get crypto into your wallet, and what each one actually requires.
        </p>
      </div>

      {/* Said plainly and first. Users otherwise spend a long time looking for
          a no-verification card purchase that does not lawfully exist. */}
      <Callout tone="info" title="Gauntlet never handles your money">
        {data?.notice}
      </Callout>

      {/* --- Swaps ---------------------------------------------------------- */}
      {swaps.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold">Already hold some crypto</h2>
            <Badge tone="gain">no account, no key</Badge>
          </div>
          <p className="text-[13px] text-ink-dim">
            The only routes here that need nothing at all — no signup, no API key, no form, no
            approval. The catch is that they are crypto-in and crypto-out, so they cannot be your
            very first purchase.
          </p>

          <div className="grid gap-3 sm:grid-cols-3">
            {swaps.map((option) => (
              <a
                key={option.id}
                href={option.url}
                target="_blank"
                rel="noreferrer noopener"
                className="panel group flex flex-col p-4 transition-colors hover:border-brass/40"
              >
                <div className="flex items-center gap-2">
                  <Repeat size={15} className="text-brass" />
                  <span className="text-sm font-medium">{option.name}</span>
                  <ArrowUpRight
                    size={13}
                    className="ml-auto text-ink-faint transition-colors group-hover:text-brass"
                  />
                </div>
                <p className="mt-2 text-xs leading-relaxed text-ink-dim">{option.summary}</p>
                {option.caveat && (
                  <p className="mt-2.5 border-t border-hairline pt-2.5 text-[11px] leading-relaxed text-ink-faint">
                    {option.caveat}
                  </p>
                )}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* --- No KYC --------------------------------------------------------- */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold">Without identity verification</h2>
          <Badge tone="gain">no KYC</Badge>
        </div>
        <p className="text-[13px] text-ink-dim">
          You buy from a person rather than a company, so no one asks for your passport. The
          trade-off is that you settle the payment yourself — nobody can automate or reverse the
          cash side for you.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {noKyc.map((option) => {
            const Icon = KIND_ICON[option.kind as keyof typeof KIND_ICON] ?? Banknote;
            return (
              <a
                key={option.id}
                href={option.url}
                target="_blank"
                rel="noreferrer noopener"
                className="panel group flex flex-col p-4 transition-colors hover:border-brass/40"
              >
                <div className="flex items-start gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-hairline bg-raised">
                    <Icon size={16} className="text-brass" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">{option.name}</span>
                      <ArrowUpRight
                        size={13}
                        className="text-ink-faint transition-colors group-hover:text-brass"
                      />
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-ink-dim">{option.summary}</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1">
                  {option.paymentMethods.slice(0, 4).map((method) => (
                    <span
                      key={method}
                      className="rounded border border-hairline px-1.5 py-0.5 text-[10px] text-ink-faint"
                    >
                      {method}
                    </span>
                  ))}
                </div>

                {option.caveat && (
                  <p className="mt-2.5 border-t border-hairline pt-2.5 text-[11px] leading-relaxed text-ink-faint">
                    {option.caveat}
                  </p>
                )}
              </a>
            );
          })}
        </div>
      </section>

      {/* --- Hosted --------------------------------------------------------- */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold">With a card or bank transfer</h2>
          <Badge tone="warn">identity check required</Badge>
        </div>

        {hosted.map((option) => (
          <Panel key={option.id}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-hairline bg-raised">
                <CreditCard size={16} className="text-brass" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{option.name}</p>
                <p className="mt-1 text-[13px] text-ink-dim">{option.summary}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {option.paymentMethods.map((method) => (
                    <span
                      key={method}
                      className="rounded border border-hairline px-1.5 py-0.5 text-[10px] text-ink-faint"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </div>
              {!option.unavailableReason && (
                <a href={option.url} target="_blank" rel="noreferrer noopener">
                  <Button variant="primary" size="sm" icon={<ArrowUpRight size={14} />}>
                    Open
                  </Button>
                </a>
              )}
            </div>

            {option.unavailableReason && (
              <div className="mt-3">
                <Callout tone="warn" title="Not set up on this instance">
                  {option.unavailableReason}
                </Callout>
              </div>
            )}

            {option.caveat && (
              <p className="mt-3 text-xs leading-relaxed text-ink-faint">{option.caveat}</p>
            )}
          </Panel>
        ))}
      </section>

      {/* --- FAQ ------------------------------------------------------------ */}
      <Panel title="Questions people ask" padded={false}>
        <ul className="divide-y divide-hairline">
          {(data?.faq ?? []).map((item, index) => (
            <li key={item.question}>
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-raised"
                aria-expanded={openFaq === index}
              >
                <Info size={14} className="shrink-0 text-ink-faint" />
                <span className="min-w-0 flex-1 text-[13px] font-medium">{item.question}</span>
                <ChevronDown
                  size={15}
                  className={cx(
                    "shrink-0 text-ink-faint transition-transform",
                    openFaq === index && "rotate-180",
                  )}
                />
              </button>
              {openFaq === index && (
                <p className="px-5 pb-4 pl-12 text-[13px] leading-relaxed text-ink-dim">
                  {item.answer}
                </p>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      <p className="text-center text-xs leading-relaxed text-ink-faint">
        These are independent services. Gauntlet does not vet them, earn anything from them, or see
        your payments. Check any address you paste, and start with a small amount.
      </p>
    </div>
  );
}
